import { ApiError } from '../core/api/http'
import { isIsoDate, nowStamp, today } from '../core/format/dates'
import { liabilities } from '../core/rules/liabilities'
import { balance, balances, codeLast4, entryProblem, expiryEntry, normalizeVoucherCode, redeemCents, validUntilFor, voucherCode, voucherProblem, voucherStatus } from '../core/rules/ledger'
import { policy } from '../data/policies'
import { db, type CreditRow, type LedgerRow, type OrderLine, type VoucherRow } from './db'
import { isGiftCard } from './pricing'
import { demoId, reply } from './reply'
import { demoAdminIdentity } from './adminIdentity'
import { toCents } from '../core/format/currency'
import { GIFTCARD_MAX_CENTS, GIFTCARD_MIN_CENTS } from '../core/rules/kassa'
import type { AdminBackend } from '../core/types/backend'
import type { CustomerCredit, LedgerEntry, Voucher, VoucherCheckResponse, VoucherSummary } from '../core/types/vouchers'

/**
 * DEMO — cadeaubonnen en tegoed aan de kant van de server (BEHEER-PLAN §3.4). Dezelfde regels als
 * de echte server (core/rules/ledger.ts): het grootboek krijgt alleen regels erbij, het saldo is
 * de som per eenheid en zakt nooit onder 0, en een verlopen bon vervalt op zijn einddatum.
 */

const PAGE_SIZE = 25
/** Grenzen voor een handmatige bon: dezelfde als een cadeaubon in de kassa (€ 1 tot € 1.000). */
const MIN_CENTS = GIFTCARD_MIN_CENTS
const MAX_CENTS = GIFTCARD_MAX_CENTS

/** Cryptografisch willekeurig, zoals de server het doet (nooit Math.random voor een code). */
const secureRandom = () => crypto.getRandomValues(new Uint32Array(1))[0] / 4294967296

const strip = ({ requestId: _r, ...e }: LedgerRow): LedgerEntry => e

function toSummary(row: VoucherRow, on: string): VoucherSummary {
  const balanceCents = balance(row.ledger, 'cents')
  const { code, note: _note, ledger: _ledger, requestId: _requestId, ...rest } = row
  return { ...rest, codeLast4: codeLast4(code), balanceCents, status: voucherStatus({ balanceCents, validUntil: row.validUntil, blocked: row.blocked }, on) }
}

const toVoucher = (row: VoucherRow, on: string): Voucher => ({ ...toSummary(row, on), code: row.code, note: row.note, ledger: row.ledger.map(strip) })

/**
 * Wat de server 's nachts doet: elke verlopen bon met saldo krijgt zijn vervalregel (op de
 * einddatum). De demo doet het bij elke aanvraag; een tweede keer verandert niets.
 */
function settle(): VoucherRow[] {
  const on = today()
  const rows = db.vouchers()
  const due = rows.some((r) => expiryEntry(r.ledger, r.validUntil, on, '') !== null)
  if (!due) return rows
  db.changeVouchers((all) =>
    all.map((r) => {
      const e = expiryEntry(r.ledger, r.validUntil, on, `${r.id}-${r.ledger.length + 1}`)
      return e ? { ...r, ledger: [...r.ledger, e] } : r
    }),
  )
  return db.vouchers()
}

function matches(row: VoucherRow, summary: VoucherSummary, q: string | undefined): boolean {
  const term = q?.trim()
  if (!term) return true
  const full = normalizeVoucherCode(term)
  if (full) return row.code === full
  const t = term.toUpperCase().replace(/[^A-Z0-9 ]/g, '')
  return summary.codeLast4 === t || (row.buyerName ?? '').toUpperCase().includes(term.toUpperCase())
}

/** Nieuwe bon met een unieke code. */
function newRow(input: Omit<VoucherRow, 'id' | 'code' | 'ledger' | 'createdAt' | 'validUntil'>, existing: VoucherRow[]): VoucherRow {
  const taken = new Set(existing.map((r) => r.code))
  let code = voucherCode(secureRandom)
  while (taken.has(code)) code = voucherCode(secureRandom)
  const createdAt = nowStamp()
  const id = demoId('bon').toLowerCase()
  const months = db.savedPolicy()?.giftCardValidMonths ?? policy.giftCardValidMonths
  return {
    ...input,
    id,
    code,
    createdAt,
    validUntil: validUntilFor(createdAt.slice(0, 10), months),
    ledger: [{ id: `${id}-1`, at: createdAt, kind: 'uitgegeven', unit: 'cents', amount: input.initialCents, ...(input.requestId ? { requestId: input.requestId } : {}) }],
  }
}

/** Standaard voor een nieuwe bon: wat het beheer laatst opsloeg (D9), anders de lokale instelling. */
const defaultValidFor = (): VoucherRow['validFor'] => db.savedPolicy()?.giftCardValidFor ?? policy.giftCardValidFor

const voucherError = (code: string, message: string) => new ApiError(message, 422, { voucherCode: message }, code)

// ——— Voor de demo-winkel (POST /vouchers/check en POST /orders) ———

/** De bon achter een ingetypte code, te gebruiken in de webshop, of een 422 zoals de server die geeft. */
export function usableVoucher(input: string): VoucherRow {
  const code = normalizeVoucherCode(input)
  const row = code ? settle().find((r) => r.code === code) : undefined
  if (!row) throw voucherError('voucher_unknown', 'Deze code kennen we niet.')
  const problem = voucherProblem({ balanceCents: balance(row.ledger, 'cents'), validUntil: row.validUntil, validFor: row.validFor, blocked: row.blocked }, today(), 'products')
  if (problem === 'voucher_expired') throw voucherError(problem, 'Deze cadeaubon is verlopen.')
  if (problem === 'voucher_blocked') throw voucherError(problem, 'Deze cadeaubon is geblokkeerd. Neem even contact met ons op.')
  if (problem === 'voucher_empty') throw voucherError(problem, 'Deze cadeaubon is al helemaal gebruikt.')
  if (problem === 'voucher_treatments_only') throw voucherError(problem, 'Deze cadeaubon is voor behandelingen in de salon.')
  return row
}

/**
 * Voor de kassa: de bon achter een code, in de salon te gebruiken (ook een behandelbon; hoeveel
 * er af mag rekent `voucherCentsFor`). Anders een 422 zoals de server die geeft.
 */
export function salonVoucher(input: string): VoucherRow {
  const code = normalizeVoucherCode(input)
  const row = code ? settle().find((r) => r.code === code) : undefined
  if (!row) throw voucherError('voucher_unknown', 'Deze code kennen we niet.')
  const problem = voucherProblem({ balanceCents: balance(row.ledger, 'cents'), validUntil: row.validUntil, validFor: row.validFor, blocked: row.blocked }, today(), 'treatments')
  if (problem === 'voucher_expired') throw voucherError(problem, 'Deze cadeaubon is verlopen.')
  if (problem === 'voucher_blocked') throw voucherError(problem, 'Deze cadeaubon is geblokkeerd.')
  if (problem === 'voucher_empty') throw voucherError(problem, 'Deze cadeaubon is al helemaal gebruikt.')
  return row
}

/** Hoeveel er bij deze bestelling van de bon af zou gaan. */
export const voucherShare = (row: VoucherRow, payableCents: number) => redeemCents(balance(row.ledger, 'cents'), payableCents)

/** Inwisselen bij een geplaatste bestelling. Zelfde bestelling nog eens: niets dubbel. */
export function redeemVoucher(voucherId: string, cents: number, orderId: string) {
  if (cents <= 0) return
  db.changeVouchers((all) =>
    all.map((r) =>
      r.id !== voucherId || r.ledger.some((e) => e.kind === 'ingewisseld' && e.orderId === orderId)
        ? r
        : { ...r, ledger: [...r.ledger, { id: `${r.id}-${r.ledger.length + 1}`, at: nowStamp(), kind: 'ingewisseld', unit: 'cents', amount: -cents, orderId }] },
    ),
  )
}

/**
 * Geannuleerd of terugbetaald: wat er van de bon af ging, komt terug (een correctie, nooit een regel
 * weghalen). `maxCents` = hooguit zoveel (een deel bij een gedeeltelijke terugbetaling); nooit meer
 * dan er nog van deze bestelling op de bon openstaat. Geeft terug hoeveel er werkelijk terugkwam.
 */
export function restoreVoucher(orderId: string, note: string, maxCents = Number.POSITIVE_INFINITY): number {
  let restored = 0
  db.changeVouchers((all) =>
    all.map((r) => {
      const used = -r.ledger.filter((e) => e.kind === 'ingewisseld' && e.orderId === orderId).reduce((s, e) => s + e.amount, 0)
      const back = r.ledger.filter((e) => e.kind === 'correctie' && e.orderId === orderId).reduce((s, e) => s + e.amount, 0)
      const left = Math.min(used - back, maxCents - restored)
      if (left <= 0) return r
      restored += left
      return { ...r, ledger: [...r.ledger, { id: `${r.id}-${r.ledger.length + 1}`, at: nowStamp(), kind: 'correctie', unit: 'cents', amount: left, note, orderId }] }
    }),
  )
  return restored
}

/** Een betaalde bestelling met cadeaubonnen: per bon een code (met de echte server: bij de betaalde webhook). */
export function issueOrderVouchers(order: { id: string; name: string; lines: OrderLine[] }) {
  const cards = order.lines.filter((l) => isGiftCard(l.productId))
  if (!cards.length || db.vouchers().some((r) => r.orderId === order.id)) return
  db.changeVouchers((all) => {
    const made: VoucherRow[] = []
    cards.forEach((l) => {
      for (let i = 0; i < l.qty; i++) {
        made.push(newRow({ initialCents: toCents(l.price), validFor: defaultValidFor(), source: 'webshop', buyerName: order.name, orderId: order.id, note: '' }, [...all, ...made]))
      }
    })
    return [...made, ...all]
  })
}

/**
 * Cadeaubonnen verkocht aan de kassa: per regel een bon met bron `kassa` (met de echte server in
 * dezelfde transactie als de bon). Geeft id, laatste vier en waarde terug, in de volgorde van de regels.
 */
export function issueKassaVouchers(
  sale: { number: string; customerName: string },
  cards: readonly { cents: number; validFor: VoucherRow['validFor'] }[],
): { id: string; last4: string; cents: number }[] {
  if (!cards.length) return []
  const made: VoucherRow[] = []
  db.changeVouchers((all) => {
    cards.forEach((c) => made.push(newRow({ initialCents: c.cents, validFor: c.validFor, source: 'kassa', buyerName: sale.customerName, note: `Verkocht op bon ${sale.number}` }, [...all, ...made])))
    return [...made, ...all]
  })
  return made.map((r) => ({ id: r.id, last4: codeLast4(r.code), cents: r.initialCents }))
}

/**
 * Een kassabon met verkochte cadeaubonnen wordt gecorrigeerd: alleen als er nog niets van af is.
 * Dan gaat het saldo eraf met een correctie (het grootboek blijft compleet). Geeft `false` als een
 * bon al (deels) gebruikt is; dan corrigeert de kassa niet.
 */
export function canCancelKassaVouchers(ids: readonly string[]): boolean {
  const rows = settle().filter((r) => ids.includes(r.id))
  return rows.every((r) => balance(r.ledger, 'cents') === r.initialCents)
}

export function cancelKassaVouchers(ids: readonly string[], note: string) {
  db.changeVouchers((all) =>
    all.map((r) => {
      const left = balance(r.ledger, 'cents')
      if (!ids.includes(r.id) || left <= 0) return r
      return { ...r, ledger: [...r.ledger, { id: `${r.id}-${r.ledger.length + 1}`, at: nowStamp(), kind: 'correctie', unit: 'cents', amount: -left, note }] }
    }),
  )
}

export const checkVoucherAnswer = (row: VoucherRow): VoucherCheckResponse => ({
  balanceCents: balance(row.ledger, 'cents'),
  validFor: row.validFor,
  validUntil: row.validUntil,
})

// ——— Klanttegoed aan de kassa ———

/** Euro-tegoed van een klant (behandelingen-tegoed telt hier niet). */
export const creditBalanceCents = (customerId: string) => balance(db.credits(customerId), 'cents')

/** Tegoed gebruikt op een kassabon: een `ingewisseld`-regel met de bon als `orderId`. Nooit dubbel. */
export function redeemCredit(customerId: string, cents: number, saleId: string, note: string) {
  if (cents <= 0 || db.credits(customerId).some((e) => e.kind === 'ingewisseld' && e.orderId === saleId)) return
  db.addCredit({ id: demoId('tg').toLowerCase(), customerId, at: nowStamp(), kind: 'ingewisseld', unit: 'cents', amount: -cents, note, orderId: saleId })
}

/** Bon gecorrigeerd: wat er van het tegoed af ging, komt terug als correctie (nooit een regel weghalen). */
export function restoreCredit(customerId: string, saleId: string, note: string) {
  const rows = db.credits(customerId).filter((e) => e.orderId === saleId && e.unit === 'cents')
  const open = -rows.reduce((s, e) => s + e.amount, 0)
  if (open <= 0) return
  db.addCredit({ id: demoId('tg').toLowerCase(), customerId, at: nowStamp(), kind: 'correctie', unit: 'cents', amount: open, note, orderId: saleId })
}

/** Gestopte kuur terugbetaald als tegoed: een `uitgegeven`-regel met de creditnota als `orderId`. Nooit dubbel. */
export function issueRefundCredit(customerId: string, cents: number, stopId: string, note: string) {
  if (cents <= 0 || db.credits(customerId).some((e) => e.kind === 'uitgegeven' && e.orderId === stopId)) return
  db.addCredit({ id: demoId('tg').toLowerCase(), customerId, at: nowStamp(), kind: 'uitgegeven', unit: 'cents', amount: cents, note, orderId: stopId })
}

// ——— Beheer ———

const creditOf = (customerId: string): CustomerCredit => {
  const ledger = db.credits(customerId).map(({ customerId: _c, ...e }) => strip(e))
  return { balance: balances(ledger), ledger: [...ledger].sort((a, b) => b.at.localeCompare(a.at)) }
}

/** Eén bon aanpassen en het nieuwe antwoord geven; 404 als hij niet (meer) bestaat. */
function changeVoucher(id: string, fn: (row: VoucherRow) => VoucherRow): Voucher {
  if (!settle().some((r) => r.id === id)) throw new ApiError('Deze cadeaubon bestaat niet meer.', 404)
  db.changeVouchers((all) => all.map((r) => (r.id === id ? fn(r) : r)))
  const row = db.vouchers().find((r) => r.id === id)
  if (!row) throw new ApiError('Deze cadeaubon bestaat niet meer.', 404)
  return toVoucher(row, today())
}

type VoucherActions = 'fetchVouchers' | 'fetchVoucher' | 'createVoucher' | 'correctVoucher' | 'blockVoucher' | 'fetchLiabilities'
type CreditActions = 'fetchCustomerCredit' | 'correctCustomerCredit'

export const demoVoucherBackend: Pick<AdminBackend, VoucherActions | CreditActions> = {
  fetchVouchers(query) {
    const on = today()
    const rows = settle()
    const all = rows.map((r) => ({ row: r, s: toSummary(r, on) }))
    // Openstaand = wat klanten nog tegoed hebben; een blokkade heft die schuld niet op.
    const outstandingCents = all.filter((x) => x.s.status === 'actief' || x.s.status === 'geblokkeerd').reduce((s, x) => s + x.s.balanceCents, 0)
    // Een collega (alleen kassa.sell) zoekt één bon op de volledige code; nooit de hele lijst of het openstaande totaal.
    if (demoAdminIdentity.access().grants['vouchers.view'] !== 'all') {
      const code = normalizeVoucherCode(query.q ?? '')
      const hit = code.length >= 8 ? all.filter((x) => x.row.code === code).map((x) => x.s) : []
      return reply({ items: hit, page: 1, pageCount: 1, total: hit.length, outstandingCents: 0 }, 300)
    }
    const hits = all
      .filter((x) => (!query.status || x.s.status === query.status) && matches(x.row, x.s, query.q))
      .sort((a, b) => b.s.createdAt.localeCompare(a.s.createdAt))
    const pageCount = Math.max(1, Math.ceil(hits.length / PAGE_SIZE))
    const page = Math.min(Math.max(1, Math.floor(query.page ?? 1)), pageCount)
    return reply({ items: hits.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((x) => x.s), page, pageCount, total: hits.length, outstandingCents }, 300)
  },

  async fetchVoucher(id) {
    const row = settle().find((r) => r.id === id)
    return reply(row ? toVoucher(row, today()) : null, 250)
  },

  async createVoucher(req) {
    // Idempotent op requestId: een herhaling (antwoord kwijt, dubbele klik) maakt nooit een tweede bon.
    const seen = db.vouchers().find((r) => r.requestId === req.requestId)
    if (seen) {
      if (seen.initialCents !== req.initialCents) throw new ApiError('Deze bon is al gemaakt met een ander bedrag.', 409, undefined, 'idempotency_conflict')
      return reply(toVoucher(seen, today()), 300)
    }
    if (!Number.isInteger(req.initialCents) || req.initialCents < MIN_CENTS || req.initialCents > MAX_CENTS) {
      throw new ApiError('Kies een bedrag van € 1 tot € 1.000.', 422, { initialCents: 'Kies een bedrag van € 1 tot € 1.000.' })
    }
    if (req.validFor !== 'all' && req.validFor !== 'treatments') throw new ApiError('Kies waarvoor de bon geldt.', 422, { validFor: 'Kies waarvoor de bon geldt.' })
    db.changeVouchers((all) => [
      newRow({ initialCents: req.initialCents, validFor: req.validFor, source: 'handmatig', note: req.note.trim().slice(0, 200), requestId: req.requestId }, all),
      ...all,
    ])
    const created = db.vouchers().find((r) => r.requestId === req.requestId)
    if (!created) throw new ApiError('De bon kon niet worden gemaakt.', 500)
    return reply(toVoucher(created, today()), 700)
  },

  async correctVoucher(id, req) {
    const row = settle().find((r) => r.id === id)
    if (!row) throw new ApiError('Deze cadeaubon bestaat niet meer.', 404)
    const seen = row.ledger.find((e) => e.requestId === req.requestId)
    if (seen) {
      if (seen.amount !== req.amountCents) throw new ApiError('Deze correctie is al verstuurd met een ander bedrag.', 409, undefined, 'idempotency_conflict')
      return reply(toVoucher(row, today()), 300)
    }
    if (voucherStatus({ balanceCents: balance(row.ledger, 'cents'), validUntil: row.validUntil }, today()) === 'verlopen') {
      throw new ApiError('Een verlopen bon kun je niet meer corrigeren.', 422, { amountCents: 'Een verlopen bon kun je niet meer corrigeren.' }, 'voucher_expired')
    }
    const reason = req.reason.trim()
    const problem = entryProblem(row.ledger, { unit: 'cents', amount: req.amountCents, kind: 'correctie', note: reason })
    if (problem) throw new ApiError(problem, 422, { [problem.includes('reden') ? 'reason' : 'amountCents']: problem })
    const next = changeVoucher(id, (r) => ({
      ...r,
      ledger: [...r.ledger, { id: `${r.id}-${r.ledger.length + 1}`, at: nowStamp(), kind: 'correctie', unit: 'cents', amount: req.amountCents, note: reason, requestId: req.requestId, ...demoAdminIdentity.actor() }],
    }))
    return reply(next, 600)
  },

  async blockVoucher(id, req) {
    const row = settle().find((r) => r.id === id)
    if (!row) throw new ApiError('Deze cadeaubon bestaat niet meer.', 404)
    // Al in de gevraagde stand: niets te doen (zo is een herhaling vanzelf idempotent).
    if (Boolean(row.blocked) === req.blocked) return reply(toVoucher(row, today()), 300)
    const reason = req.reason.trim()
    if (req.blocked && !reason) throw new ApiError('Vul een reden in.', 422, { reason: 'Vul een reden in.' })
    const next = changeVoucher(id, (r) => ({ ...r, blocked: req.blocked ? { at: nowStamp(), reason: reason.slice(0, 200) } : null }))
    return reply(next, 500)
  },

  async fetchLiabilities(on) {
    // Een stand op het eind van `on` (nooit later dan vandaag): alleen grootboekregels tot en met die dag.
    const day = isIsoDate(on) && on < today() ? on : today()
    const upTo = <T extends { at: string }>(list: readonly T[]) => list.filter((e) => e.at.slice(0, 10) <= day)
    const vouchers = settle()
      .filter((r) => r.createdAt.slice(0, 10) <= day)
      .map((r) => toSummary({ ...r, ledger: upTo(r.ledger) }, day))
    const accounts = db.accounts()
    // Kuren hebben (nog) geen historie: de demo geeft de stand van nu. De server rekent op `on`.
    const courses = accounts.flatMap((a) => (a.course ? [a.course] : []))
    const credits = accounts.map((a) => upTo(db.credits(a.customer.id)))
    return reply(liabilities({ vouchers, courses, credits }, day), 350)
  },

  async fetchCustomerCredit(id) {
    if (!db.account(id)) throw new ApiError('Deze klant bestaat niet meer.', 404)
    return reply(creditOf(id), 250)
  },

  async correctCustomerCredit(id, req) {
    if (!db.account(id)) throw new ApiError('Deze klant bestaat niet meer.', 404)
    const current = db.credits(id)
    const seen = current.find((c) => c.requestId === req.requestId)
    if (seen) {
      if (seen.amount !== req.amount || seen.unit !== req.unit) throw new ApiError('Deze correctie is al verstuurd met andere gegevens.', 409, undefined, 'idempotency_conflict')
      return reply(creditOf(id), 300)
    }
    if (req.unit !== 'cents' && req.unit !== 'treatment') throw new ApiError('Kies euro’s of behandelingen.', 422, { unit: 'Kies euro’s of behandelingen.' })
    const reason = req.reason.trim()
    const problem = entryProblem(current, { unit: req.unit, amount: req.amount, kind: 'correctie', note: reason })
    if (problem) throw new ApiError(problem, 422, { [problem.includes('reden') ? 'reason' : 'amount']: problem })
    const row: CreditRow = { id: demoId('tg').toLowerCase(), customerId: id, at: nowStamp(), kind: 'correctie', unit: req.unit, amount: req.amount, note: reason, requestId: req.requestId, ...demoAdminIdentity.actor() }
    db.addCredit(row)
    return reply(creditOf(id), 600)
  },
}
